import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  Label
} from '@bliks-io/ui';
import { PARTNER_MATERIAL_LOCALES, type PartnerMaterialLocale } from '@bliks-io/contracts';
import { PARTNER_MATERIAL_LOCALE_NAMES } from '@/lib/partner-material-text';

const isLocale = (value: unknown): value is PartnerMaterialLocale =>
  PARTNER_MATERIAL_LOCALES.includes(value as PartnerMaterialLocale);

function matchesLanguage(locale: PartnerMaterialLocale, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return (
    !needle ||
    locale.includes(needle) ||
    PARTNER_MATERIAL_LOCALE_NAMES[locale].toLowerCase().includes(needle)
  );
}

interface PartnerLanguagePickerProps {
  value: PartnerMaterialLocale;
  onChange: (locale: PartnerMaterialLocale) => void;
}

/**
 * One searchable field instead of a row of language buttons, so the toolbar
 * keeps its size however many languages the material comes in.
 */
export function PartnerLanguagePicker({ value, onChange }: PartnerLanguagePickerProps) {
  const { t } = useTranslation();
  const id = useId();
  const [search, setSearch] = useState('');
  const matches = useMemo(
    () => PARTNER_MATERIAL_LOCALES.filter((locale) => matchesLanguage(locale, search)),
    [search]
  );

  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="shrink-0">
        {t('partner.infoHub.language')}
      </Label>
      <Combobox
        value={value}
        multiple={false}
        onValueChange={(next) => {
          if (isLocale(next)) onChange(next);
          setSearch('');
        }}
      >
        <ComboboxTrigger id={id} className="w-44">
          <span className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            {PARTNER_MATERIAL_LOCALE_NAMES[value]}
          </span>
        </ComboboxTrigger>
        <ComboboxContent align="end">
          <ComboboxInput
            placeholder={t('partner.infoHub.searchLanguage')}
            value={search}
            onValueChange={setSearch}
          />
          <ComboboxList>
            <ComboboxEmpty>{t('partner.infoHub.noLanguage')}</ComboboxEmpty>
            {matches.map((locale) => (
              <ComboboxItem key={locale} value={locale}>
                <span className="flex flex-1 items-center justify-between gap-2">
                  {PARTNER_MATERIAL_LOCALE_NAMES[locale]}
                  <span className="text-xs uppercase text-muted-foreground">{locale}</span>
                </span>
              </ComboboxItem>
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
